import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Shield,
  ArrowRight,
  Calculator,
  FileText,
  ShieldCheck,
  ClipboardList,
  Download as DownloadIcon,
} from "lucide-react";
import { toast } from "sonner";
import { usePwaInstall } from "@/hooks/usePwaInstall";

const SPLASH_BG = "https://static.prod-images.emergentagent.com/jobs/aadda98d-1ed6-4c61-92dc-fe4380e92a37/images/0cee94cc3a8c8e2b9c2aa915b80a2f7dfa1a0a636d39af4e0f98d13b2d5d84b0.png";

// MANUAL-ONLY landing gate.
//
// Behaviour:
// - The splash ALWAYS renders when the user lands on "/".
// - There is NO useEffect, NO setTimeout, NO setInterval, NO animation-end
//   listener, NO conditional render that auto-redirects the user.
// - Only `handleEnterRigSentinel()` (invoked by the Enter button click /
//   tap / keyboard Enter / Space) may write to sessionStorage and may call
//   `navigate("/home")`.
//
// To avoid being instantly auto-entered by stale data from earlier broken
// versions of this page, we use a brand-new session key (v2) and proactively
// purge the legacy v1 key on import.
export const LANDING_SESSION_KEY = "rsc.landing.manualEnter.v2";
const LEGACY_KEY_V1 = "rsc.landing.entered.v1";

// Eagerly purge the legacy stale flag so it can never drive auto-entry. Safe
// in private-mode (sessionStorage may throw).
try { sessionStorage.removeItem(LEGACY_KEY_V1); } catch { /* ignore */ }
try { localStorage.removeItem(LEGACY_KEY_V1); } catch { /* ignore */ }
try { localStorage.removeItem(LANDING_SESSION_KEY); } catch { /* ignore */ }

export function hasEnteredLanding() {
  try { return sessionStorage.getItem(LANDING_SESSION_KEY) === "true"; }
  catch { return false; }
}

export function markLandingEntered() {
  try { sessionStorage.setItem(LANDING_SESSION_KEY, "true"); } catch { /* ignore */ }
}

const FEATURE_BADGES = [
  { id: "calculations", label: "Calculations", icon: Calculator },
  { id: "reports", label: "Reports", icon: FileText },
  { id: "bop-test-records", label: "BOP Test Records", icon: ShieldCheck },
  { id: "field-documentation", label: "Field Documentation", icon: ClipboardList },
];

// Exported so diagnostic tests can introspect badge content without relying on
// String(Component) → JSX-source heuristics.
export const SPLASH_FEATURE_BADGES = FEATURE_BADGES;

// Splash content strings exported for robust diagnostic introspection.
export const SPLASH_TITLE = "RigSentinel";
export const SPLASH_EYEBROW = "FIELD OPERATIONS SUITE";
export const SPLASH_SUBTITLE = "CALCULATIONS \u2022 REPORTS \u2022 FIELD DOCUMENTATION";
export const SPLASH_DESCRIPTION = "A field-ready operations companion for drilling teams, supervisors, mud engineers, cementers, well-control personnel, and oilfield personnel.";
export const SPLASH_ENTER_LABEL = "ENTER RIGSENTINEL";
export const SPLASH_SAFETY_FOOTER = "For calculation and documentation support only. Verify all results, reports, and field documents before operational use.";

export default function Splash() {
  const navigate = useNavigate();
  const { installed, canPromptInstall, showIOSInstructions, promptInstall } = usePwaInstall();

  // The ONLY entry path. Called by mouse click, touch, keyboard Enter / Space.
  const handleEnterRigSentinel = () => {
    markLandingEntered();
    navigate("/home");
  };

  const onButtonKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleEnterRigSentinel();
    }
  };

  // Secondary action beneath Enter — Android/desktop trigger the native
  // prompt directly; iOS has no programmatic install, so it routes to
  // /download, which already shows the Add-to-Home-Screen steps (kept out
  // of Splash to avoid duplicating that instructional UI here).
  const handleInstallClick = async () => {
    if (canPromptInstall) {
      const outcome = await promptInstall();
      if (outcome === "accepted") toast.success("RigSentinel installed");
      return;
    }
    if (showIOSInstructions) navigate("/download");
  };

  return (
    <div
      data-testid="splash-screen"
      className="splash-root min-h-screen relative overflow-x-hidden flex flex-col items-center justify-between gap-8 text-white"
      style={{ minHeight: "100dvh", overflowY: "auto" }}
      onKeyDown={(e) => { if (e.key === "Enter") handleEnterRigSentinel(); }}
      tabIndex={-1}
    >
      <div
        className="absolute inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: `url(${SPLASH_BG})` }}
      />
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,11,14,0.45) 0%, rgba(10,11,14,0.85) 60%, #0A0B0E 100%)",
        }}
      />

      <div
        className="splash-top pt-8 sm:pt-14 px-5 sm:px-6 w-full max-w-md text-center"
        style={{ flex: "1 1 auto" }}
      >
        <div
          data-testid="splash-eyebrow"
          className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--rs-text-3)] mb-6"
          style={{ letterSpacing: "0.3em" }}
        >
          <span className="block w-6 h-px bg-[var(--rs-amber)]" />
          {SPLASH_EYEBROW}
          <span className="block w-6 h-px bg-[var(--rs-amber)]" />
        </div>

        <img
          data-testid="splash-logo"
          src="/rigsentinel-logo.png"
          alt="RigSentinel — The information you need. The decisions you own."
          className="logo block mx-auto mb-6 h-auto w-3/4 sm:w-2/3 md:w-3/5"
          decoding="async"
          fetchPriority="high"
        />

        <p
          data-testid="splash-subtitle"
          className="font-mono-rs text-[11px] tracking-[0.18em] uppercase mt-3 text-[var(--rs-text-2)]"
        >
          {SPLASH_SUBTITLE}
        </p>
        <p
          data-testid="splash-description"
          className="text-sm text-[var(--rs-text-2)] mt-5 leading-relaxed max-w-sm mx-auto"
        >
          {SPLASH_DESCRIPTION}
        </p>

        <div className="hidden-mobile">
          <div
            data-testid="splash-feature-badges"
            className="badges flex flex-wrap items-center justify-center gap-1.5 mt-5"
          >
            {FEATURE_BADGES.map(({ id, label, icon: Icon }) => (
              <span
                key={id}
                data-testid={`splash-feature-badge-${id}`}
                className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase px-2 py-1 rounded-[4px]"
                style={{
                  background: "rgba(22,24,29,0.55)",
                  color: "var(--rs-text-2)",
                  border: "1px solid var(--rs-border)",
                }}
              >
                <Icon className="w-3 h-3" style={{ color: "var(--rs-amber)" }} />
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        className="splash-bottom w-full max-w-md px-5 sm:px-6 pb-8 sm:pb-10 flex flex-col gap-3"
        style={{ flex: "0 0 auto" }}
      >
        <button
          data-testid="splash-enter-btn"
          onClick={handleEnterRigSentinel}
          onKeyDown={onButtonKeyDown}
          autoFocus
          className="btn-amber w-full h-12 rounded-md label-up text-xs inline-flex items-center justify-center gap-2"
        >
          {SPLASH_ENTER_LABEL}
          <ArrowRight className="w-4 h-4" />
        </button>
        {!installed && (canPromptInstall || showIOSInstructions) && (
          <button
            data-testid="splash-install-btn"
            onClick={handleInstallClick}
            className="btn-ghost w-full h-11 rounded-md label-up text-[11px] inline-flex items-center justify-center gap-2"
          >
            <DownloadIcon className="w-3.5 h-3.5" /> INSTALL RIGSENTINEL
          </button>
        )}
        <div
          data-testid="splash-safety-footer"
          className="flex items-start gap-2 text-[11px] text-[var(--rs-text-3)]"
        >
          <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--rs-amber)" }} />
          <span>{SPLASH_SAFETY_FOOTER}</span>
        </div>
        <nav
          data-testid="splash-legal-row"
          aria-label="Legal and commercial"
          className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1.5 text-[10px] tracking-[0.10em] uppercase font-semibold text-[var(--rs-text-3)] pt-1"
        >
          <Link to="/pricing" data-testid="splash-link-pricing" className="hover:text-[var(--rs-amber)] transition-colors">Pricing</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms-of-service" data-testid="splash-link-terms" className="hover:text-[var(--rs-amber)] transition-colors">Terms</Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy-policy" data-testid="splash-link-privacy" className="hover:text-[var(--rs-amber)] transition-colors">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/refund-policy" data-testid="splash-link-refund" className="hover:text-[var(--rs-amber)] transition-colors">Refund</Link>
          <span aria-hidden="true">·</span>
          <Link to="/disclaimer" data-testid="splash-link-disclaimer" className="hover:text-[var(--rs-amber)] transition-colors">Disclaimer</Link>
          <span aria-hidden="true">·</span>
          <Link to="/contact" data-testid="splash-link-contact" className="hover:text-[var(--rs-amber)] transition-colors">Contact</Link>
        </nav>
        <div className="hidden-mobile">
          <address
            data-testid="splash-business-address"
            className="not-italic text-center text-[11px] leading-relaxed text-[var(--rs-text-3)] pt-1"
          >
            <div className="font-semibold text-[var(--rs-text-2,#9aa6b2)]">RigSentinel</div>
            <div>180 Mill Street, Toronto, ON M4M 0V8, Canada</div>
            <div>
              Contact:{" "}
              <a
                href="mailto:rigsentinel@gmail.com"
                data-testid="splash-business-email"
                className="underline hover:text-[var(--rs-amber)] transition-colors"
              >
                rigsentinel@gmail.com
              </a>
            </div>
          </address>
        </div>
      </div>
    </div>
  );
}
